import { createFileRoute, Link } from "@tanstack/react-router";
import { formatPrice, productSlug } from "@/lib/format";
import type { SearchProductRow } from "@/lib/products.functions";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plus, ShoppingCart, Camera, Loader2, Send, RotateCcw } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ProductCard, ProductCardSkeleton } from "@/components/storefront/ProductCard";
import { useSearchIndex } from "@/hooks/storefront/use-search-index";
import { useCart, CART_ADD_EVENT } from "@/hooks/storefront/use-cart";
import { imageToSearchQuery } from "@/lib/ai-search.functions";
import { orderPandakiResults } from "@/lib/pandaki-order";
import { pandakiChat } from "@/lib/pandaki.functions";

export const Route = createFileRoute("/pandaki")({
  head: () => ({
    meta: [
      { title: "Pandaki — Ψώνισε συζητώντας με τον βοηθό του MrPanda" },
      { name: "description", content: "Γράψε τι σκέφτεσαι, το Pandaki σε ρωτά, σου δείχνει 12 προϊόντα και τα βάζεις στο καλάθι με ένα κλικ." },
      { property: "og:title", content: "Pandaki — AI βοηθός αγορών MrPanda" },
      { property: "og:description", content: "Ψώνισε συζητώντας: πες τι θέλεις και πάρε έτοιμες προτάσεις προϊόντων." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: PandakiPage,
});

type Msg = { role: "user" | "assistant"; text: string; options?: string[]; kind?: "restart"; q?: string };

const GREETING: Msg = {
  role: "assistant",
  text: "Γεια! Είμαι το Pandaki 🐼 Πες μου τι σου αρέσει ή τι χρειάζεσαι και θα σου βρω τα καλύτερα!",
  options: ["Σοκολάτα 🍫", "Καφές ☕", "Σνακ για ταινία 🍿", "Απορρυπαντικό 🧺", "Δώρο για παιδί 🎁"],
};

/** Ερώτηση που εμφανίζεται ευθύς αμέσως μόλις μπει κάτι στο καλάθι. */
const RESTART_ASK: Msg = {
  role: "assistant",
  kind: "restart",
  text: "Μπράβο, μπήκε στο καλάθι! 🛒 Βρήκες αυτό που ήθελες; Θέλεις να ξεκινήσουμε από την αρχή για να ψάξουμε κάτι άλλο;",
};

function PandakiPage() {
  const [messages, setMessages] = useState<Msg[]>([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [query, setQuery] = useState("");
  const { run, isReady } = useSearchIndex(true);
  const cart = useCart();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);

  // Αν η φράση είναι πολύ συγκεκριμένη, αφαιρούμε λέξεις (από το τέλος, μετά από την αρχή) μέχρι να βρεθούν προϊόντα.
  const findProducts = useCallback((query: string) => {
    if (!query || !isReady) return [];
    const words = query.split(/\s+/).filter(Boolean);
    const tries: string[] = [query];
    for (let n = words.length - 1; n >= 1; n--) {
      tries.push(words.slice(0, n).join(" "));
      tries.push(words.slice(words.length - n).join(" "));
    }
    for (let i = 0; i < words.length; i++) {
      for (let j = i + 1; j < words.length; j++) tries.push(`${words[i]} ${words[j]}`);
    }
    for (const q of tries) {
      // Μεγαλύτερη δεξαμενή υποψηφίων: έτσι φτάνουν δίπλα και τα άμεσα διαθέσιμα
      // που δεν βγήκαν στις πρώτες 12 θέσεις συνάφειας.
      const items = run(q, 60).items;
      if (items.length) return orderPandakiResults(items).slice(0, 12);
    }
    return [];
  }, [isReady, run]);
  const products = useMemo(() => findProducts(query), [findProducts, query]);

  // Η σελίδα δεν σκρολάρει: κυλούν μόνο η συζήτηση και τα προϊόντα.
  useEffect(() => {
    const h = document.documentElement;
    const prev = [h.style.overflow, document.body.style.overflow];
    h.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      h.style.overflow = prev[0];
      document.body.style.overflow = prev[1];
    };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  // Μόλις μπει κάτι στο καλάθι, ρωτάμε αν θέλει νέα αναζήτηση από την αρχή.
  useEffect(() => {
    const onAdd = () =>
      setMessages((m) => {
        if (m[m.length - 1]?.kind === "restart") return m;
        return [...m, RESTART_ASK];
      });
    window.addEventListener(CART_ADD_EVENT, onAdd);
    return () => window.removeEventListener(CART_ADD_EVENT, onAdd);
  }, []);

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;
    const next: Msg[] = [...messages, { role: "user", text }];
    setMessages(next);
    setInput("");
    setBusy(true);

    // Πρώτα ψάχνουμε αυτούσιο το μήνυμα στο κατάστημα: αν είναι μάρκα/προϊόν (π.χ. «Overlay λίπη»)
    // δείχνουμε αμέσως τα προϊόντα και ενημερώνουμε το AI ότι υπάρχουν.
    const norm = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const hit = isReady ? run(text, 12) : null;
    const direct = hit && hit.items.length > 0 && hit.mode !== "fuzzy" ? hit : null;
    const namedBrand = direct?.brands.find((b) => b.name && norm(text).includes(norm(b.name)))?.name ?? null;
    if (direct) setQuery(text);
    const catalog = direct
      ? {
          brands: direct.brands.slice(0, 8).map((b) => b.name.slice(0, 80)),
          titles: direct.items.slice(0, 8).map((p) => (p.name ?? "").slice(0, 160)).filter(Boolean),
        }
      : undefined;

    try {
      const { result, error } = await pandakiChat({
        data: { messages: next.slice(-20).map((m) => ({ role: m.role, text: m.text.slice(0, 600) })), catalog },
      });
      if (result) {
        setMessages((m) => [...m, { role: "assistant", text: result.reply, options: result.options }]);
        let q = result.searchQuery;
        // Η μάρκα που έγραψε ο πελάτης δεν πρέπει να χαθεί από την αναζήτηση.
        if (q && namedBrand && !norm(q).includes(norm(namedBrand))) {
          const withBrand = `${namedBrand} ${q}`;
          q = run(withBrand, 1).items.length ? withBrand : text;
        }
        if (q && isReady && !run(q, 1).items.length && direct) q = text;
        if (q) setQuery(q);
        const finalQ = q || (direct ? text : "");
        if (finalQ) setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, q: finalQ } : x)));
      } else {
        setQuery(text);
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            q: text,
            text:
              error === "rate"
                ? "Ουπς, μιλάμε πολύ γρήγορα! Περίμενε λίγο και ξαναδοκίμασε 🐼"
                : "Δεν μπόρεσα να σκεφτώ τώρα, αλλά έψαξα αυτό που έγραψες 👉",
          },
        ]);
      }
    } catch {
      setQuery(text);
      setMessages((m) => [...m, { role: "assistant", q: text, text: "Κάτι πήγε στραβά, αλλά έψαξα αυτό που έγραψες 👉" }]);
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  const reset = () => {
    setMessages([GREETING]);
    setQuery("");
    inputRef.current?.focus();
  };

  /** Μικραίνει τη φωτογραφία (max 1024px, JPEG) ώστε να χωρά στο όριο του αιτήματος. */
  const fileToDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1024 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d")?.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(img.src);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => reject(new Error("bad image"));
      img.src = URL.createObjectURL(file);
    });

  /** Ο πελάτης ανεβάζει/τραβά φωτογραφία προϊόντος· το AI την αναγνωρίζει και ψάχνουμε το προϊόν. */
  const onPhoto = async (file: File | null) => {
    if (!file || busy || photoBusy) return;
    setPhotoBusy(true);
    setMessages((m) => [...m, { role: "user", text: "📷 Έστειλα φωτογραφία προϊόντος" }]);
    try {
      const dataUrl = await fileToDataUrl(file);
      const { query: q, error } = await imageToSearchQuery({ data: { dataUrl } });
      if (q) {
        setQuery(q);
        setMessages((m) => [
          ...m,
          { role: "assistant", q, text: `Α, το βλέπω! 👀 Ψάχνω για «${q}» — δες τι βρήκα!` },
        ]);
      } else {
        setMessages((m) => [
          ...m,
          {
            role: "assistant",
            text:
              error === "rate"
                ? "Ουπς, πολλές φωτογραφίες μαζί! Περίμενε λίγο και ξαναδοκίμασε 🐼"
                : "Δεν κατάφερα να αναγνωρίσω προϊόν στη φωτογραφία 🙈 Δοκίμασε πιο καθαρή λήψη ή γράψε μου τι ψάχνεις!",
          },
        ]);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Κάτι πήγε στραβά με τη φωτογραφία — δοκίμασε ξανά ή γράψε μου τι ψάχνεις!" }]);
    } finally {
      setPhotoBusy(false);
      if (photoRef.current) photoRef.current.value = "";
    }
  };

  /** Επικόλληση εικόνας (Ctrl+V) μέσα στο chat. */
  const onPaste = (e: React.ClipboardEvent) => {
    const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith("image/"));
    if (file) {
      e.preventDefault();
      onPhoto(file);
    }
  };

  /** Drag & drop εικόνας πάνω στο chat. */
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = Array.from(e.dataTransfer?.files ?? []).find((f) => f.type.startsWith("image/"));
    if (file) onPhoto(file);
  };

  /** Η ερώτηση «από την αρχή» κλείνει χωρίς να σβήσει η συζήτηση. */
  const dismissRestart = () =>
    setMessages((m) => m.map((x) => (x.kind === "restart" ? { ...x, kind: undefined } : x)));

  const last = messages[messages.length - 1];

  return (
    <StorefrontLayout pinnedCart>
      <div className="relative z-10 mx-auto flex h-[calc(100dvh-var(--mp-header-h,64px))] w-full max-w-none flex-col px-1 py-1 sm:px-5 lg:h-full lg:pt-3 lg:pb-20">
        <h1 className="sr-only">🐼 Pandaki</h1>

        <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(300px,380px)_1fr]">
          {/* Chat */}
          <section
            className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm lg:rounded-3xl"
            onDragOver={(e) => {
              e.preventDefault();
              if (e.dataTransfer?.types?.includes("Files")) setDragOver(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false);
            }}
            onDrop={onDrop}
          >
            {dragOver && (
              <div className="pointer-events-none absolute inset-0 z-20 grid place-items-center rounded-3xl border-4 border-dashed border-primary bg-primary/15 backdrop-blur-sm">
                <div className="text-center font-bold text-primary">
                  <div className="mb-1 text-4xl animate-bounce">📷</div>
                  Άφησε τη φωτογραφία εδώ!
                </div>
              </div>
            )}
            <div className="flex items-center justify-between border-b border-border bg-primary/10 px-2 py-1 lg:px-4 lg:py-2.5">
              <span className="text-xs font-bold lg:text-sm">🐼 Pandaki</span>
              <button onClick={reset} className="flex items-center gap-1 rounded-full px-2 py-1 text-xs text-muted-foreground hover:bg-muted" aria-label="Νέα συζήτηση">
                <RotateCcw className="h-3.5 w-3.5" /> Από την αρχή
              </button>
            </div>
            <div ref={scrollRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-2 lg:space-y-3 lg:p-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex animate-fade-in ${m.role === "user" ? "justify-end" : "justify-start gap-2"}`}>
                  {m.role === "assistant" && <span className="mt-0.5 text-base lg:mt-1 lg:text-xl">🐼</span>}
                  <div className="min-w-0 max-w-[88%]">
                    <div
                      className={`rounded-2xl px-2.5 py-1.5 text-[13px] leading-snug lg:px-3.5 lg:py-2 lg:text-sm lg:leading-relaxed ${
                        m.role === "user" ? "rounded-br-sm bg-primary text-primary-foreground" : "rounded-bl-sm bg-muted text-foreground"
                      }`}
                    >
                      {m.text}
                    </div>
                    {m.q && <InlineResults items={findProducts(m.q).slice(0, 3) as InlineItem[]} ready={isReady} />}
                    {m.kind === "restart" && i === messages.length - 1 && !busy && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          onClick={reset}
                          className="mp-bouncy rounded-full border-2 border-primary bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition hover:-translate-y-0.5"
                        >
                          Ναι, ξεκίνα από την αρχή 🔄
                        </button>
                        <button
                          onClick={dismissRestart}
                          className="mp-bouncy rounded-full border-2 border-primary/40 bg-background px-3 py-1.5 text-xs font-semibold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-primary-foreground"
                        >
                          Όχι, τα βρήκα 👍
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(busy || photoBusy) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="text-xl animate-bounce">🐼</span>
                  <span className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.3s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:-0.15s]" />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-primary" />
                  </span>
                </div>
              )}
              {!busy && last.role === "assistant" && last.options && last.options.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pl-6 lg:gap-2 lg:pl-8">
                  {last.options.map((o) => (
                    <button
                      key={o}
                      onClick={() => send(o)}
                      className="mp-bouncy rounded-full border-2 border-primary/40 bg-background px-3 py-1.5 text-xs font-semibold text-primary transition hover:-translate-y-0.5 hover:bg-primary hover:text-primary-foreground"
                    >
                      {o}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex items-end gap-1.5 border-t border-border p-1.5 lg:gap-2 lg:p-3"
            >
              <input
                ref={photoRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onPhoto(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                onClick={() => photoRef.current?.click()}
                disabled={busy || photoBusy}
                aria-label="Αναζήτηση με φωτογραφία"
                title="Ανέβασε ή τράβα φωτογραφία προϊόντος"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-primary/40 bg-background text-primary shadow-sm transition hover:scale-105 hover:bg-primary hover:text-primary-foreground disabled:opacity-50"
              >
                {photoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
              </button>
              <textarea
                ref={inputRef}
                autoFocus
                rows={1}
                value={input}
                maxLength={600}
                onChange={(e) => setInput(e.target.value)}
                onPaste={onPaste}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send(input);
                  }
                }}
                placeholder="Τι ψάχνεις;"
                className="max-h-28 min-h-10 flex-1 resize-none rounded-2xl border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Αποστολή"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow transition hover:scale-105 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event("mp:open-mobile-cart"))}
                aria-label="Άνοιγμα καλαθιού"
                className="relative grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow lg:hidden"
              >
                <ShoppingCart className="h-4 w-4" />
                {cart.count > 0 && (
                  <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-background px-0.5 text-[10px] font-bold text-primary ring-1 ring-primary">
                    {cart.count}
                  </span>
                )}
              </button>
            </form>
            <p className="hidden px-3 pb-2 text-[10px] text-muted-foreground lg:block">Σύρε ή επικόλλησε (Ctrl+V) φωτογραφία προϊόντος για αναζήτηση · Το Pandaki χρησιμοποιεί AI — έλεγξε τα στοιχεία πριν την αγορά.</p>
          </section>

          {/* Προϊόντα */}
          <section className="hidden lg:block lg:min-h-0 lg:overflow-y-auto lg:pr-1">
            {query && (
              <>
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-lg font-bold">✨ Βρήκα για σένα: <span className="text-primary">{query}</span></h2>
                </div>
                {!isReady ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                    {Array.from({ length: 8 }).map((_, i) => <ProductCardSkeleton key={i} />)}
                  </div>
                ) : products.length === 0 ? (
                  <div className="rounded-3xl border-2 border-dashed border-border p-10 text-center text-muted-foreground">
                    <div className="mb-2 text-4xl">🙈</div>
                    Δεν βρήκα κάτι για «{query}». Πες μου με άλλα λόγια!
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                    {products.map((p) => <ProductCard key={p.id} product={p as never} />)}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>
    </StorefrontLayout>
  );
}

/** Στο κινητό: 3 μαζεμένα προϊόντα μέσα στην απάντηση του chat. */
type InlineItem = SearchProductRow;
function InlineResults({ items, ready }: { items: InlineItem[]; ready: boolean }) {
  const cart = useCart();
  if (!ready) return null;
  if (!items.length) return <p className="mt-1 text-[11px] text-muted-foreground lg:hidden">Δεν βρήκα κάτι — πες το αλλιώς!</p>;
  return (
    <ul className="mt-1.5 space-y-1 lg:hidden">
      {items.map((p) => {
        const sale = p.original_price != null && Number(p.original_price) > Number(p.price);
        return (
          <li key={p.id} className="flex items-center gap-2 rounded-xl border border-border bg-background p-1.5">
            <Link to="/p/$slug" params={{ slug: productSlug(p.name ?? "", p.ps_id, p.id) }} className="flex min-w-0 flex-1 items-center gap-2">
              <img src={p.image_url ?? ""} alt={p.name ?? ""} loading="lazy" className="h-12 w-12 shrink-0 rounded-lg bg-card object-contain" />
              <span className="min-w-0 flex-1">
                <span className="line-clamp-2 text-[12px] font-medium leading-tight">{p.name}</span>
                <span className="mt-0.5 flex items-baseline gap-1.5 text-[12px]">
                  <b className={sale ? "text-destructive" : "text-primary"}>{formatPrice(Number(p.price))}</b>
                  {sale && <s className="text-[10px] text-muted-foreground">{formatPrice(Number(p.original_price))}</s>}
                </span>
              </span>
            </Link>
            <button
              type="button"
              aria-label={`Προσθήκη ${p.name ?? ""} στο καλάθι`}
              onClick={() =>
                cart.add({
                  productId: p.id, psId: p.ps_id, name: p.name, price: Number(p.price), imageUrl: p.image_url,
                  originalPrice: sale ? Number(p.original_price) : null, stock: p.stock_quantity,
                  taxRate: Number(p.tax_rate ?? 24), deposit: null,
                } as never)
              }
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow"
            >
              <Plus className="h-4 w-4" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}
